import React from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { offersPageInvalidateOfferDialogState } from "store/OffersPageStore";
import { useInvalidateOfferMutation } from "state/queries/useOffers";
import { useLoadOffers } from "store/ProposalsPageHooks";

export const InvalidateOfferDialog: React.FC = () => {
  const { t } = useTranslation();
  const loadOffers = useLoadOffers();
  const [dialogState, setDialogState] = useAtom(
    offersPageInvalidateOfferDialogState
  );

  const invalidateMutation = useInvalidateOfferMutation(() => {
    // On success, close dialog and refresh the offers list
    setDialogState({ isOpen: false, offer: null });
    loadOffers({
      skip: 0,
      top: 40,
      reset: true,
    });
  });

  const handleClose = () => {
    setDialogState({ isOpen: false, offer: null });
  };

  const handleConfirm = () => {
    if (dialogState.offer?.OfferId) {
      invalidateMutation.mutate(dialogState.offer.OfferId);
    }
  };

  return (
    <Dialog
      open={dialogState.isOpen}
      onOpenChange={(e, data) => !data.open && handleClose()}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleClose}
              />
            }
          >
            {t("qtg.invalidateOffer.dialog.title")}
          </DialogTitle>
          <DialogContent>
            <p>
              {t("qtg.invalidateOffer.dialog.message", {
                offerId: dialogState.offer?.OfferId,
              })}
            </p>
            <p style={{ marginTop: "1rem" }}>
              {t("qtg.invalidateOffer.dialog.confirmation")}
            </p>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              {t("qtg.common.cancel")}
            </Button>
            <Button
              data-help="offers-invalidate-confirm"
              appearance="primary"
              onClick={handleConfirm}
              disabled={invalidateMutation.isPending}
            >
              {invalidateMutation.isPending
                ? t("qtg.common.processing")
                : t("qtg.invalidateOffer.dialog.confirm")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
